import { createRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod/v4';
import { queryClient } from '@/App.tsx';
import { appRoute } from '@/features/dashboard/_root/routes/appRoute.tsx';
import { isMobileViewport } from '@/hooks/use-mobile.tsx';
import { getTourQueryOptions } from '@/features/dashboard/tours/hooks/useGetTour.ts';
import { TourTab } from '@/features/dashboard/tours/routes/tourTabs.ts';
import { tourFinancesSearchSchema } from '@/features/dashboard/tours/routes/tourFinanceView.ts';
import { listSearchSchema } from '@/components/table/routes/listSearchParams.ts';
import TourDetailsPage from '@/features/dashboard/tours/pages/TourDetailsPage.tsx';
import TourTabMenu from '@/features/dashboard/tours/pages/TourTabMenu.tsx';
import TourSummaryTab from '@/features/dashboard/tours/pages/TourSummaryTab.tsx';
import TourFinancesTab from '@/features/dashboard/tours/pages/TourFinancesTab.tsx';
import TourOptionsTab from '@/features/dashboard/tours/pages/TourOptionsTab.tsx';
import TourStatsTab from '@/features/dashboard/tours/pages/TourStatsTab.tsx';
import TourParticipantsTable from '@/features/dashboard/tours/components/TourParticipantsTable.tsx';
import TourClientsTable from '@/features/dashboard/tours/components/TourClientsTable.tsx';
import TourMessagesTable from '@/features/dashboard/tours/components/TourMessagesTable.tsx';
import TourWaitlistTable from '@/features/dashboard/waitlist/components/TourWaitlistTable.tsx';

/**
 * Ekran wyjazdu. Route nadrzędny to szkielet — nagłówek, tagi i modale;
 * każda zakładka ma własny segment ścieżki i własny route potomny.
 *
 * `justCreated` zostaje parametrem wyszukiwania, bo dotyczy całego ekranu:
 * modal powitalny pokazuje się niezależnie od tego, którą zakładkę widać.
 */
export const tourRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tours/$tourId',
  component: TourDetailsPage,
  loader: ({ params }) => {
    const { tourId } = params;
    return queryClient.fetchQuery(getTourQueryOptions(tourId));
  },
  validateSearch: z.object({
    justCreated: z.boolean().optional(),
  }),
});

/**
 * Adres bez segmentu zakładki. Tak nawigują wiersze tabel i tak wyglądają
 * linki sprzed tej zmiany.
 *
 * Szeroko prowadzi na „Podsumowanie”. Poniżej `md` jest osobnym stanem —
 * ekranem-menu z kaflami zakładek — więc przekierowania tam nie ma; zabrałoby
 * jedyny adres, pod który da się z zakładki wrócić.
 */
export const tourIndexRoute = createRoute({
  getParentRoute: () => tourRoute,
  path: '/',
  beforeLoad: ({ params, search }) => {
    if (isMobileViewport()) return;

    throw redirect({
      to: '/app/tours/$tourId/summary',
      params,
      search,
      replace: true,
    });
  },
  component: TourTabMenu,
});

export const tourSummaryRoute = createRoute({
  getParentRoute: () => tourRoute,
  path: TourTab.SUMMARY,
  component: TourSummaryTab,
});

export const tourParticipantsRoute = createRoute({
  getParentRoute: () => tourRoute,
  path: TourTab.PARTICIPANTS,
  component: TourParticipantsTable,
  validateSearch: listSearchSchema,
});

export const tourClientsRoute = createRoute({
  getParentRoute: () => tourRoute,
  path: TourTab.CLIENTS,
  component: TourClientsTable,
  validateSearch: listSearchSchema,
});

export const tourWaitlistRoute = createRoute({
  getParentRoute: () => tourRoute,
  path: TourTab.WAITLIST,
  component: TourWaitlistTable,
  validateSearch: listSearchSchema,
});

/**
 * Wpłaty i budżet to dwa spojrzenia na te same finanse, nie dwie podstrony —
 * przekrój siedzi więc w parametrze wyszukiwania, a nie w segmencie ścieżki.
 */
export const tourFinancesRoute = createRoute({
  getParentRoute: () => tourRoute,
  path: TourTab.FINANCES,
  component: TourFinancesTab,
  validateSearch: tourFinancesSearchSchema,
});

export const tourOptionsRoute = createRoute({
  getParentRoute: () => tourRoute,
  path: TourTab.OPTIONS,
  component: TourOptionsTab,
});

export const tourStatsRoute = createRoute({
  getParentRoute: () => tourRoute,
  path: TourTab.STATS,
  component: TourStatsTab,
});

export const tourMessagesRoute = createRoute({
  getParentRoute: () => tourRoute,
  path: TourTab.MESSAGES,
  component: TourMessagesTable,
  validateSearch: listSearchSchema,
});
